import { apiClient } from '@/lib/api-client';
import type {
  FlashcardDeck,
  Flashcard,
  DeckCreate,
  DeckUpdate,
  CardCreate,
  CardUpdate,
  VocabularyDeckCreate,
  WordDefinition,
  WordPreviewResponse,
  StudySession,
  SessionSummary,
  FlashcardProgress,
  DeckProgress,
  RecordAnswerRequest,
  StartSessionRequest,
  UserStatistics,
  CacheStatistics,
} from '../types/flashcard';

/**
 * Flashcard API Client
 * All endpoints are prefixed with /flashcards
 */

// ==================== Deck Management ====================

export const flashcardApi = {
  // Decks
  async getDecks(params?: { deck_type?: string; theme?: string; include_public?: boolean }): Promise<FlashcardDeck[]> {
    return apiClient.get<FlashcardDeck[]>('/flashcards/decks', { params });
  },

  async getDeck(deckId: string): Promise<FlashcardDeck> {
    return apiClient.get<FlashcardDeck>(`/flashcards/decks/${deckId}`);
  },

  async createDeck(data: DeckCreate): Promise<FlashcardDeck> {
    return apiClient.post<FlashcardDeck>('/flashcards/decks', data);
  },

  async updateDeck(deckId: string, data: DeckUpdate): Promise<FlashcardDeck> {
    return apiClient.put<FlashcardDeck>(`/flashcards/decks/${deckId}`, data);
  },

  async deleteDeck(deckId: string): Promise<void> {
    return apiClient.delete(`/flashcards/decks/${deckId}`);
  },

  // ==================== Card Management ====================

  async getCards(deckId: string): Promise<Flashcard[]> {
    return apiClient.get<Flashcard[]>(`/flashcards/decks/${deckId}/cards`);
  },

  async getCard(cardId: string): Promise<Flashcard> {
    return apiClient.get<Flashcard>(`/flashcards/cards/${cardId}`);
  },

  async createCard(deckId: string, data: CardCreate): Promise<Flashcard> {
    return apiClient.post<Flashcard>(`/flashcards/decks/${deckId}/cards`, data);
  },

  async createCardsBulk(deckId: string, cards: CardCreate[]): Promise<Flashcard[]> {
    return apiClient.post<Flashcard[]>(`/flashcards/decks/${deckId}/cards/bulk`, { cards });
  },

  async updateCard(cardId: string, data: CardUpdate): Promise<Flashcard> {
    return apiClient.put<Flashcard>(`/flashcards/cards/${cardId}`, data);
  },

  async deleteCard(cardId: string): Promise<void> {
    return apiClient.delete(`/flashcards/cards/${cardId}`);
  },

  async reorderCards(deckId: string, cardIds: string[]): Promise<void> {
    return apiClient.post(`/flashcards/decks/${deckId}/cards/reorder`, { card_ids: cardIds });
  },

  // ==================== Vocabulary ====================

  async lookupWord(
    word: string,
    storyId?: string,
    creditToStudentId?: string,
  ): Promise<WordDefinition> {
    return apiClient.post<WordDefinition>('/flashcards/vocabulary/lookup', {
      word,
      ...(storyId && { story_id: storyId }),
      ...(creditToStudentId && { credit_to_student_id: creditToStudentId }),
    });
  },

  async previewWords(words: string[]): Promise<WordPreviewResponse> {
    return apiClient.post('/flashcards/vocabulary/preview', { words });
  },

  async createVocabularyDeck(data: VocabularyDeckCreate): Promise<FlashcardDeck> {
    return apiClient.post<FlashcardDeck>('/flashcards/vocabulary/decks', data);
  },

  async getStoryVocabDeck(storyId: string): Promise<FlashcardDeck> {
    return apiClient.get<FlashcardDeck>(`/flashcards/vocabulary/story/${storyId}/deck`);
  },

  async getCacheStatistics(): Promise<CacheStatistics> {
    return apiClient.get<CacheStatistics>('/flashcards/vocabulary/cache/statistics');
  },

  async clearCache(words?: string[]): Promise<{ cleared_count: number }> {
    return apiClient.delete<{ cleared_count: number }>('/flashcards/vocabulary/cache', { words });
  },

  // ==================== Study Sessions ====================

  async startSession(data: StartSessionRequest): Promise<StudySession> {
    return apiClient.post<StudySession>('/flashcards/study/sessions', data);
  },

  async getSession(sessionId: string): Promise<StudySession> {
    return apiClient.get<StudySession>(`/flashcards/study/sessions/${sessionId}`);
  },

  async getSessionSummary(sessionId: string): Promise<SessionSummary> {
    return apiClient.get<SessionSummary>(`/flashcards/study/sessions/${sessionId}/summary`);
  },

  async completeSession(sessionId: string): Promise<StudySession> {
    return apiClient.post<StudySession>(`/flashcards/study/sessions/${sessionId}/complete`, {});
  },

  async sessionHeartbeat(sessionId: string): Promise<void> {
    return apiClient.patch(`/flashcards/study/sessions/${sessionId}/heartbeat`);
  },

  async getSessions(params?: { deck_id?: string; limit?: number }): Promise<StudySession[]> {
    return apiClient.get<StudySession[]>('/flashcards/study/sessions', { params });
  },

  // ==================== Progress & Answers ====================

  async recordAnswer(cardId: string, data: RecordAnswerRequest): Promise<FlashcardProgress> {
    return apiClient.post<FlashcardProgress>(`/flashcards/study/cards/${cardId}/answer`, data);
  },

  async getCardProgress(cardId: string): Promise<FlashcardProgress> {
    return apiClient.get<FlashcardProgress>(`/flashcards/study/progress/cards/${cardId}`);
  },

  async getDeckProgress(deckId: string): Promise<DeckProgress> {
    return apiClient.get<DeckProgress>(`/flashcards/study/progress/decks/${deckId}`);
  },

  async getCardsDueForReview(deckId?: string): Promise<Flashcard[]> {
    return apiClient.get<Flashcard[]>('/flashcards/study/progress/due-for-review', {
      params: { deck_id: deckId },
    });
  },

  async getUserStatistics(): Promise<UserStatistics> {
    return apiClient.get<UserStatistics>('/flashcards/study/progress/statistics');
  },

  async resetCardProgress(cardId: string): Promise<void> {
    return apiClient.delete(`/flashcards/study/progress/cards/${cardId}`);
  },
};
